import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import PortalTopbar from "../components/PortalTopbar";
import PortalAlert from "../components/PortalAlert";
import { useSignOutHref } from "../components/useSignOutHref";
import "../components/PortalButton.css";
import "./NoAccessPage.css";

type AuthMeResponse = {
  clientPrincipal: null | {
    userDetails: string;
    identityProvider: string;
  };
};

export default function NoAccessPage() {
  const nav = useNavigate();
  const [email, setEmail] = useState<string | null>(null);
  const [authLoaded, setAuthLoaded] = useState(false);
  const signOutHref = useSignOutHref();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const meRes = await fetch("/.auth/me", { credentials: "include" });
        const me = (await meRes.json()) as AuthMeResponse;
        if (cancelled) return;
        setEmail(me?.clientPrincipal?.userDetails || null);
      } catch {
        if (!cancelled) setEmail(null);
      } finally {
        if (!cancelled) setAuthLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="na-shell">
      <PortalTopbar showSignOut showPortalLogin={false} />

      <main className="na-main" id="main">
        <div className="na-card">
          <header className="na-header">
            <span className="na-eyebrow">Portal access</span>
            <h1 className="na-title">You&apos;re signed in, but not set up yet</h1>
            <p className="na-subtitle">
              Your account doesn&apos;t have an approved role in AcceptingMD yet.
              Pick the path that matches your role to continue.
            </p>
            {authLoaded && email ? (
              <p className="na-identity">
                Signed in as <strong>{email}</strong>
              </p>
            ) : null}
          </header>

          <div className="na-paths" role="list">
            <article className="na-path na-path--primary" role="listitem">
              <div className="na-path-head">
                <div className="na-path-icon" aria-hidden="true">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"
                    strokeLinejoin="round">
                    <path d="M3 21h18" />
                    <path d="M5 21V7l7-4 7 4v14" />
                    <path d="M9 9h1" />
                    <path d="M9 13h1" />
                    <path d="M9 17h1" />
                    <path d="M14 9h1" />
                    <path d="M14 13h1" />
                    <path d="M14 17h1" />
                  </svg>
                </div>
                <div className="na-path-titles">
                  <h2 className="na-path-title">Clinic Admin</h2>
                  <p className="na-path-sub">
                    Includes solo physicians running their own practice.
                  </p>
                </div>
              </div>
              <p className="na-path-body">
                Request Clinic Admin access to <strong>join an existing
                clinic</strong> or <strong>submit a new clinic</strong> for
                approval. A Super Admin reviews every request manually.
              </p>
              <button
                type="button"
                onClick={() => nav("/portal/request-access")}
                className="pb-btn pb-btn--primary pb-btn--full"
              >
                Request Clinic Admin access
              </button>
            </article>

            <article className="na-path na-path--muted" role="listitem">
              <div className="na-path-head">
                <div className="na-path-icon" aria-hidden="true">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"
                    strokeLinejoin="round">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                    <circle cx="8.5" cy="7" r="4" />
                    <polyline points="17 11 19 13 23 9" />
                  </svg>
                </div>
                <div className="na-path-titles">
                  <h2 className="na-path-title">Provider</h2>
                  <p className="na-path-sub">
                    Family Doctor, Specialist, or Allied Health.
                  </p>
                </div>
              </div>
              <p className="na-path-body">
                Providers don&apos;t request access directly. Your{" "}
                <strong>Clinic Admin must invite you</strong> after the clinic is
                approved &mdash; you&apos;ll receive an email with a join link.
              </p>
              <div className="na-path-note" role="note">
                No action needed. Contact your Clinic Admin if you&apos;re waiting.
              </div>
            </article>
          </div>

          <div className="na-pending">
            <span className="na-pending-text">
              Already submitted a Clinic Admin request?
            </span>
            <button
              type="button"
              onClick={() => nav("/portal/access-pending")}
              className="pb-btn pb-btn--ghost"
            >
              Check pending status
            </button>
          </div>

          <PortalAlert tone="warn" title="Safety reminder">
            Do not submit any patient information anywhere in the portal access
            flow. Use it only for your own clinic or provider credentials.
          </PortalAlert>

          <p className="na-support">
            Think you should already have access? Contact your Clinic Admin or
            your system administrator.
          </p>

          <div className="na-footer">
            <a href={signOutHref} className="na-text-link">
              Sign out
            </a>
            <span className="na-footer-sep" aria-hidden="true">·</span>
            <Link to="/public" className="na-text-link">
              Go to public site
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
